import { FlagIcon, ShieldOffIcon } from 'lucide-react-native';
import * as React from 'react';
import { Modal, Pressable, ScrollView, TextInput, View } from 'react-native';

import { androidStatusBarHeight, ModalStatusBarBand } from '@/components/android-status-bar-band';
import { Button } from '@sixam/ui/button';
import { Icon } from '@sixam/ui/icon';
import { Text } from '@sixam/ui/text';
import {
  blockMember,
  REPORT_REASONS,
  reportContent,
  type ReportReason,
  type ReportSubject,
} from '@/lib/moderation';

type ReportContentSheetProps = Readonly<{
  /** Shown on the confirmation, so the member knows what they just reported. */
  contentLabel: string;
  isPresented: boolean;
  onDismiss: () => void;
  /** Called after a block, so a screen can drop the member from view at once. */
  onBlocked?: () => void;
  subjectId: string;
  subjectType: ReportSubject;
  /** The author. Without one there is nobody to block, so only reporting shows. */
  subjectUserId?: string | null;
}>;

/**
 * Reporting content, and blocking the member who posted it.
 *
 * Both live in one sheet because they are the same moment for the person using
 * them: something is wrong and they want it gone. Splitting them across two
 * screens is how one of them stops being used.
 */
export function ReportContentSheet({
  contentLabel,
  isPresented,
  onBlocked,
  onDismiss,
  subjectId,
  subjectType,
  subjectUserId,
}: ReportContentSheetProps) {
  const [reason, setReason] = React.useState<ReportReason | null>(null);
  const [details, setDetails] = React.useState('');
  const [isWorking, setIsWorking] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [done, setDone] = React.useState<'blocked' | 'reported' | null>(null);

  // Reset on the modal's own show event rather than an effect watching a prop:
  // it fires once, when the sheet actually appears.
  function resetForNewReport() {
    setReason(null);
    setDetails('');
    setError(null);
    setDone(null);
  }

  async function submitReport() {
    if (!reason) {
      setError('Choose what is wrong with this content.');
      return;
    }
    setIsWorking(true);
    setError(null);
    try {
      await reportContent({ details, reason, subjectId, subjectType });
      setDone('reported');
    } catch (reportError) {
      setError(reportError instanceof Error ? reportError.message : 'Could not send the report.');
    } finally {
      setIsWorking(false);
    }
  }

  async function submitBlock() {
    if (!subjectUserId) {
      return;
    }
    setIsWorking(true);
    setError(null);
    try {
      await blockMember(subjectUserId, details || undefined);
      setDone('blocked');
      onBlocked?.();
    } catch (blockError) {
      setError(blockError instanceof Error ? blockError.message : 'Could not block this member.');
    } finally {
      setIsWorking(false);
    }
  }

  return (
    <Modal
      animationType="slide"
      onRequestClose={onDismiss}
      onShow={resetForNewReport}
      presentationStyle="pageSheet"
      statusBarTranslucent
      visible={isPresented}
    >
      <View className="flex-1 bg-background" style={{ paddingTop: androidStatusBarHeight() }}>
        <ModalStatusBarBand />
        <ScrollView contentContainerClassName="gap-5 p-6">
          {done ? (
            <View className="gap-3">
              <Text variant="h2">
                {done === 'blocked' ? 'Member blocked' : 'Thanks for telling us'}
              </Text>
              <Text>
                {done === 'blocked'
                  ? 'You will not see this member or their content again. The club organisers have been notified and will review them.'
                  : 'The club organisers have been notified. We review every report and act within 24 hours.'}
              </Text>
              <Button onPress={onDismiss}>
                <Text>Done</Text>
              </Button>
            </View>
          ) : (
            <>
              <View className="gap-2">
                <Text variant="h2">Report content</Text>
                <Text variant="muted">
                  Reporting {contentLabel}. 6am has no tolerance for objectionable content or
                  abusive members — we review every report and act within 24 hours.
                </Text>
              </View>

              <View className="gap-2">
                <Text variant="label" className="text-muted-foreground">
                  What is wrong?
                </Text>
                {REPORT_REASONS.map((option) => (
                  <Pressable
                    accessibilityRole="radio"
                    accessibilityState={{ checked: reason === option.value }}
                    className="min-h-14 justify-center border-b border-border py-3 active:opacity-60"
                    key={option.value}
                    onPress={() => setReason(option.value)}
                  >
                    <Text className={reason === option.value ? 'font-bold text-primary' : ''}>
                      {option.label}
                    </Text>
                  </Pressable>
                ))}
              </View>

              <View className="gap-2">
                <Text variant="label" className="text-muted-foreground">
                  Anything else? (optional)
                </Text>
                <TextInput
                  accessibilityLabel="Report details"
                  className="min-h-24 rounded-md border-2 border-border bg-card p-3 text-foreground"
                  multiline
                  onChangeText={setDetails}
                  placeholder="What should we know?"
                  placeholderTextColor="#71717A"
                  textAlignVertical="top"
                  value={details}
                />
              </View>

              {error ? (
                <Text className="text-sm text-destructive" selectable>
                  {error}
                </Text>
              ) : null}

              <Button disabled={isWorking} onPress={() => void submitReport()}>
                <Icon as={FlagIcon} className="size-5 text-primary-foreground" />
                <Text>{isWorking ? 'Sending…' : 'Send report'}</Text>
              </Button>

              {subjectUserId ? (
                <View className="gap-2 border-t border-border pt-5">
                  <Text variant="label" className="text-muted-foreground">
                    Block this member
                  </Text>
                  <Text variant="muted">
                    They disappear from your feed straight away and cannot reach you. The organisers
                    are notified so they can review the account.
                  </Text>
                  <Button
                    disabled={isWorking}
                    onPress={() => void submitBlock()}
                    variant="destructive"
                  >
                    <Icon as={ShieldOffIcon} className="size-5 text-primary-foreground" />
                    <Text>{isWorking ? 'Blocking…' : 'Block member'}</Text>
                  </Button>
                </View>
              ) : null}

              <Button onPress={onDismiss} variant="secondary">
                <Text>Cancel</Text>
              </Button>
            </>
          )}
        </ScrollView>
      </View>
    </Modal>
  );
}
